import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';
import './CompanyRegisterPage.css';

function CompanyRegisterPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [saveMessage, setSaveMessage] = useState('');

    const [formData, setFormData] = useState({
        // 기본 정보
        companyName: '',
        companyCode: '',
        schemaName: '',
        businessNumber: '',
        representativeName: '',
        // 연락처 정보
        contactEmail: '',
        contactPhone: '',
        address: '',
        // 구독 정보
        subscriptionPlan: 'BASIC',
        subscriptionStatus: 'TRIAL',
        maxUsers: 10,
        maxEquipments: 20,
        // 관리자 계정 정보
        adminUsername: '',
        adminEmail: '',
        adminPassword: '',
        adminPasswordConfirm: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // 에러 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // 회사코드 입력 시 스키마명 자동 생성 (소문자만 허용)
    const handleCompanyCodeChange = (e) => {
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '');
        setFormData(prev => ({
            ...prev,
            companyCode: value,
            schemaName: value || ''
        }));
        if (errors.companyCode) {
            setErrors(prev => ({ ...prev, companyCode: '' }));
        }
    };

    // 사업자등록번호 자동 포맷 (숫자만 입력 → 123-45-67890 형식)
    const handleBusinessNumberChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
        let formatted = value;
        if (value.length > 3 && value.length <= 5) {
            formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
        } else if (value.length > 5) {
            formatted = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
        }
        setFormData(prev => ({
            ...prev,
            businessNumber: formatted
        }));
        if (errors.businessNumber) {
            setErrors(prev => ({ ...prev, businessNumber: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // 기본 정보 검증
        if (!formData.companyName.trim()) {
            newErrors.companyName = '회사명을 입력해주세요';
        }
        if (!formData.companyCode.trim()) {
            newErrors.companyCode = '회사코드를 입력해주세요';
        } else if (formData.companyCode.length < 2 || formData.companyCode.length > 20) {
            newErrors.companyCode = '회사코드는 2~20자여야 합니다';
        }
        if (!formData.representativeName.trim()) {
            newErrors.representativeName = '대표자명을 입력해주세요';
        }
        if (formData.businessNumber && !/^\d{3}-\d{2}-\d{5}$/.test(formData.businessNumber)) {
            newErrors.businessNumber = '사업자번호 형식이 올바르지 않습니다 (예: 123-45-67890)';
        }

        // 연락처 정보 검증
        if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
            newErrors.contactEmail = '이메일 형식이 올바르지 않습니다';
        }
        if (formData.contactPhone && !/^\d{2,3}-\d{3,4}-\d{4}$/.test(formData.contactPhone)) {
            newErrors.contactPhone = '연락처 형식이 올바르지 않습니다 (예: 02-1234-5678)';
        }

        // 구독 정보 검증
        if (!formData.maxUsers || formData.maxUsers < 1) {
            newErrors.maxUsers = '최소 1명 이상이어야 합니다';
        }
        if (!formData.maxEquipments || formData.maxEquipments < 1) {
            newErrors.maxEquipments = '최소 1대 이상이어야 합니다';
        }

        // 관리자 계정 검증
        if (!formData.adminUsername.trim()) {
            newErrors.adminUsername = '관리자 아이디(이메일)를 입력해주세요';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminUsername)) {
            newErrors.adminUsername = '이메일 형식이 올바르지 않습니다';
        }
        if (!formData.adminEmail.trim()) {
            newErrors.adminEmail = '관리자 이메일을 입력해주세요';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
            newErrors.adminEmail = '이메일 형식이 올바르지 않습니다';
        }
        if (!formData.adminPassword) {
            newErrors.adminPassword = '비밀번호를 입력해주세요';
        } else if (formData.adminPassword.length < 8) {
            newErrors.adminPassword = '비밀번호는 8자 이상이어야 합니다';
        }
        if (formData.adminPassword !== formData.adminPasswordConfirm) {
            newErrors.adminPasswordConfirm = '비밀번호가 일치하지 않습니다';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setSaveMessage('');

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    companyName: formData.companyName,
                    companyCode: formData.companyCode,
                    schemaName: formData.schemaName,
                    businessNumber: formData.businessNumber || null,
                    representativeName: formData.representativeName,
                    contactEmail: formData.contactEmail || null,
                    contactPhone: formData.contactPhone || null,
                    address: formData.address || null,
                    subscriptionPlan: formData.subscriptionPlan,
                    subscriptionStatus: formData.subscriptionStatus,
                    maxUsers: parseInt(formData.maxUsers),
                    maxEquipments: parseInt(formData.maxEquipments),
                    adminUsername: formData.adminUsername,
                    adminEmail: formData.adminEmail,
                    adminPassword: formData.adminPassword
                })
            });

            const result = await response.json();

            if (result.success) {
                setSaveMessage('회사가 성공적으로 등록되었습니다.');
                setTimeout(() => {
                    navigate('/platform/companies');
                }, 1500);
            } else {
                setSaveMessage(result.error || '회사 등록에 실패했습니다.');
            }
        } catch (error) {
            console.error('회사 등록 실패:', error);
            setSaveMessage('회사 등록 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/platform/companies');
    };

    return (
        <div className="company-register-page">
            <div className="company-register-header">
                <div className="company-register-header-left">
                    <button className="back-btn" onClick={handleCancel}>
                        <span className="material-symbols-outlined">arrow_back_ios</span>
                    </button>
                    <div>
                        <h1 className="company-register-title">회사 등록</h1>
                        <p className="company-register-subtitle">새로운 테넌트(회사)를 등록하고 관리자 계정을 생성합니다</p>
                    </div>
                </div>
            </div>

            <form className="company-register-form" onSubmit={handleSubmit}>
                {/* 기본 정보 */}
                <div className="form-section">
                    <h3 className="section-title">기본 정보</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">
                                회사명 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                className={`form-input ${errors.companyName ? 'error' : ''}`}
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder="회사명을 입력하세요"
                            />
                            {errors.companyName && <span className="error-message">{errors.companyName}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                회사코드 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyCode"
                                className={`form-input ${errors.companyCode ? 'error' : ''}`}
                                value={formData.companyCode}
                                onChange={handleCompanyCodeChange}
                                placeholder="영문 소문자, 숫자만 입력 (예: company1)"
                                maxLength={20}
                            />
                            {errors.companyCode && <span className="error-message">{errors.companyCode}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">스키마명</label>
                            <input
                                type="text"
                                name="schemaName"
                                className="form-input readonly"
                                value={formData.schemaName}
                                readOnly
                                placeholder="회사코드 입력 시 자동 생성"
                            />
                            <span className="form-hint">회사코드 기반으로 자동 생성됩니다</span>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                대표자명 <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="representativeName"
                                className={`form-input ${errors.representativeName ? 'error' : ''}`}
                                value={formData.representativeName}
                                onChange={handleChange}
                                placeholder="대표자명을 입력하세요"
                            />
                            {errors.representativeName && <span className="error-message">{errors.representativeName}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">사업자등록번호</label>
                            <input
                                type="text"
                                name="businessNumber"
                                className={`form-input ${errors.businessNumber ? 'error' : ''}`}
                                value={formData.businessNumber}
                                onChange={handleBusinessNumberChange}
                                placeholder="숫자만 입력"
                            />
                            {errors.businessNumber && <span className="error-message">{errors.businessNumber}</span>}
                        </div>
                    </div>
                </div>

                {/* 연락처 정보 */}
                <div className="form-section">
                    <h3 className="section-title">연락처 정보</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">대표 이메일</label>
                            <input
                                type="email"
                                name="contactEmail"
                                className={`form-input ${errors.contactEmail ? 'error' : ''}`}
                                value={formData.contactEmail}
                                onChange={handleChange}
                                placeholder="company@example.com"
                            />
                            {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">대표 연락처</label>
                            <input
                                type="text"
                                name="contactPhone"
                                className={`form-input ${errors.contactPhone ? 'error' : ''}`}
                                value={formData.contactPhone}
                                onChange={handleChange}
                                placeholder="02-1234-5678"
                            />
                            {errors.contactPhone && <span className="error-message">{errors.contactPhone}</span>}
                        </div>

                        <div className="form-group form-group-full">
                            <label className="form-label">주소</label>
                            <input
                                type="text"
                                name="address"
                                className="form-input"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="회사 주소를 입력하세요"
                            />
                        </div>
                    </div>
                </div>

                {/* 구독 정보 */}
                <div className="form-section">
                    <h3 className="section-title">구독 정보</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">구독 플랜</label>
                            <div className="select-container">
                                <select
                                    name="subscriptionPlan"
                                    className="form-select"
                                    value={formData.subscriptionPlan}
                                    onChange={handleChange}
                                >
                                    <option value="BASIC">Basic</option>
                                    <option value="STANDARD">Standard</option>
                                    <option value="PREMIUM">Premium</option>
                                    <option value="ENTERPRISE">Enterprise</option>
                                </select>
                                <span className="material-symbols-outlined select-icon">expand_more</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">구독 상태</label>
                            <div className="select-container">
                                <select
                                    name="subscriptionStatus"
                                    className="form-select"
                                    value={formData.subscriptionStatus}
                                    onChange={handleChange}
                                >
                                    <option value="TRIAL">Trial (체험)</option>
                                    <option value="ACTIVE">Active (활성)</option>
                                    <option value="SUSPENDED">Suspended (중지)</option>
                                </select>
                                <span className="material-symbols-outlined select-icon">expand_more</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                최대 사용자 수 <span className="required">*</span>
                            </label>
                            <input
                                type="number"
                                name="maxUsers"
                                className={`form-input ${errors.maxUsers ? 'error' : ''}`}
                                value={formData.maxUsers}
                                onChange={handleChange}
                                min="1"
                            />
                            {errors.maxUsers && <span className="error-message">{errors.maxUsers}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                최대 설비 수 <span className="required">*</span>
                            </label>
                            <input
                                type="number"
                                name="maxEquipments"
                                className={`form-input ${errors.maxEquipments ? 'error' : ''}`}
                                value={formData.maxEquipments}
                                onChange={handleChange}
                                min="1"
                            />
                            {errors.maxEquipments && <span className="error-message">{errors.maxEquipments}</span>}
                        </div>
                    </div>
                </div>

                {/* 관리자 계정 */}
                <div className="form-section">
                    <h3 className="section-title">관리자 계정</h3>
                    <p className="section-description">회사 등록 시 함께 생성될 관리자 계정 정보입니다.</p>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">
                                관리자 아이디 (이메일) <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                name="adminUsername"
                                className={`form-input ${errors.adminUsername ? 'error' : ''}`}
                                value={formData.adminUsername}
                                onChange={handleChange}
                                placeholder="admin@example.com"
                                autoComplete="off"
                            />
                            {errors.adminUsername && <span className="error-message">{errors.adminUsername}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                관리자 이메일 <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                name="adminEmail"
                                className={`form-input ${errors.adminEmail ? 'error' : ''}`}
                                value={formData.adminEmail}
                                onChange={handleChange}
                                placeholder="admin@example.com"
                                autoComplete="off"
                            />
                            {errors.adminEmail && <span className="error-message">{errors.adminEmail}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                비밀번호 <span className="required">*</span>
                            </label>
                            <input
                                type="password"
                                name="adminPassword"
                                className={`form-input ${errors.adminPassword ? 'error' : ''}`}
                                value={formData.adminPassword}
                                onChange={handleChange}
                                placeholder="8자 이상 입력"
                                autoComplete="new-password"
                            />
                            {errors.adminPassword && <span className="error-message">{errors.adminPassword}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                비밀번호 확인 <span className="required">*</span>
                            </label>
                            <input
                                type="password"
                                name="adminPasswordConfirm"
                                className={`form-input ${errors.adminPasswordConfirm ? 'error' : ''}`}
                                value={formData.adminPasswordConfirm}
                                onChange={handleChange}
                                placeholder="비밀번호를 다시 입력"
                                autoComplete="new-password"
                            />
                            {errors.adminPasswordConfirm && <span className="error-message">{errors.adminPasswordConfirm}</span>}
                        </div>
                    </div>
                </div>

                {/* 버튼 영역 */}
                <div className="form-actions">
                    {saveMessage && (
                        <span className={`save-message ${saveMessage.includes('실패') || saveMessage.includes('오류') ? 'error' : 'success'}`}>
                            {saveMessage}
                        </span>
                    )}
                    <button type="button" className="btn-cancel" onClick={handleCancel} disabled={loading}>
                        취소
                    </button>
                    <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? '등록 중...' : '회사 등록'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CompanyRegisterPage;
